<template>
  <div class="app-container">
    <TopNav />
    <div class="layout">
      <SideNav class="side-nav" />
      <div class="main-content">
        <ModalNuevoProfesional ref="modalnuevoprofesional" />

        <div class="main-container">
          <div v-if="loaded" class="content-card">
            <div class="card-header">
              <label for="professional-search">Buscar profesional</label>
              <input
                id="professional-search"
                v-model="busqueda"
                type="search"
                class="form-control"
                placeholder="Buscar por nombre o teléfono"
              >
            </div>
            <div class="card-body">
              <table class="table table-hover">
                <thead class="table-light">
                  <tr>
                    <th>Nombre</th>
                    <th>Teléfono</th>
                    <th>Calificación</th>
                    <th>Puntaje fraude</th>
                    <th>Balance puntos Jobbing</th>
                    <th>Estado</th>
                    <th>Clases restringidas</th>
                    <!-- New column shows only the accepted ratio -->
                    <th>Presupuestos</th>
                    <th>Tributario / SII</th>
                    <th class="text-end">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr 
                    v-for="p in profesionalesFiltrados"
                    :key="p.profesional.id" 
                    :class="{'suspended-row': p.profesional.suspendido == 1}"
                  >
                    <td>
                      <img 
                        class="avatar" 
                        :src="p.profesional.foto ? p.profesional.foto : 'https://cdn.icon-icons.com/icons2/1378/PNG/512/avatardefault_92824.png'" 
                        alt="Imagen profesional"
                      >
                      <router-link :to="'VerProfesional/'+p.profesional.id" class="prof-name">
                        {{ p.profesional.nombre + ' ' + p.profesional.apellido }}
                      </router-link>
                    </td>
                    <td class="text-nowrap">
                      <span v-if="p.profesional.telefono">{{ p.profesional.telefono }}</span>
                      <span v-else class="text-muted">Sin teléfono</span>
                    </td>
                    <td>
                      <div v-if="p.clasificacion[0] === 0">
                        <em>Sin calificaciones</em>
                      </div>
                      <div v-else>
                        <span 
                          v-for="s in Math.trunc(p.clasificacion[1] / p.clasificacion[0])" 
                          :key="s"
                        >
                          <b-icon class="star" icon="star-fill"></b-icon>
                        </span>
                        <span v-if="(p.clasificacion[1] / p.clasificacion[0]) % 1 >= 0.5">
                          <b-icon class="star" icon="star-half"></b-icon>
                        </span>
                        <span 
                          v-for="s in Math.floor(5 - (p.clasificacion[1] / p.clasificacion[0]))" 
                          :key="s"
                        >
                          <b-icon class="star" icon="star"></b-icon>
                        </span>
                        <span>
                          ({{ (p.clasificacion[1] / p.clasificacion[0]).toFixed(2) }}, {{ p.clasificacion[0] }} órdenes)
                        </span>
                      </div>
                    </td>
                    <td>
                      <span v-if="hasFraudScore(p.profesional)">
                        {{ formatFraudScore(p.profesional) }}
                      </span>
                      <span v-else class="text-muted">Sin registro</span>
                    </td>
                    <td>
                      <span class="points-balance">{{ formatJobbingPoints(getJobbingPointsBalance(p.profesional)) }}</span>
                      <small class="points-unit">pts</small>
                    </td>
                    <td>
                      <div>
                        <span
                          v-if="p.profesional.atendiendo_orden"
                          class="badge badge-success"
                        >
                          Atendiendo <b-icon icon="eye"></b-icon>
                        </span>
                        <span 
                          v-else 
                          class="badge badge-info"
                        >
                          Libre
                        </span>
                        <span 
                          v-if="p.profesional.suspendido == 1" 
                          class="badge badge-warning"
                        >
                          Suspendido
                        </span>
                        <span 
                          v-if="p.profesional.oculto == 1" 
                          class="badge badge-secondary"
                        >
                          Oculto
                        </span>
                        <span 
                          v-if="p.profesional.solicitudes_privadas != 1" 
                          class="badge badge-third"
                        >
                          Sin Privadas
                        </span>
                        <span 
                          v-if="p.profesional.visibilidad_restringida == 1" 
                          class="badge badge-dark"
                        >
                          Visibilidad Restringida
                        </span>
                        <span
                          v-if="p.profesional.visibilidad_restringida_manual == 1"
                          class="badge badge-danger"
                        >
                          Restringido Manual
                        </span>
                        <span
                          v-if="p.profesional.solo_compra_solicitudes == 1"
                          class="badge badge-points"
                        >
                          Solo Compra
                        </span>
                      </div>
                    </td>
                    <td>
                      <div>
                        <small class="text-muted d-block">Restringidas:</small>
                        <div
                          v-if="p.profesional.clases_restringidas && p.profesional.clases_restringidas.length"
                          class="restricted-class-list"
                        >
                          <span
                            v-for="className in p.profesional.clases_restringidas"
                            :key="`restricted-${p.profesional.id}-${className}`"
                            class="badge restricted-class-badge"
                          >
                            {{ formatRestrictedClassName(className) }}
                          </span>
                        </div>
                        <span v-else class="text-muted">Sin restricciones</span>
                      </div>
                      <div class="mt-2">
                        <small class="text-muted d-block">Autoresponder:</small>
                        <div
                          v-if="p.profesional.autoresponder_clases && p.profesional.autoresponder_clases.length"
                          class="restricted-class-list"
                        >
                          <span
                            v-for="className in p.profesional.autoresponder_clases"
                            :key="`autoresponder-${p.profesional.id}-${className}`"
                            class="badge restricted-class-badge"
                          >
                            {{ formatRestrictedClassName(className) }}
                          </span>
                        </div>
                        <span v-else class="text-muted">Sin filtro</span>
                      </div>
                    </td>
                    <!-- New Presupuestos column: display ratio and tooltip -->
                    <td>
                      <div v-if="p.profesional.presupuesto_count > 0" class="css-tooltip">
                        <span>{{ ratioText(p.profesional) }}</span>
                        <div class="tooltip-content">
                          {{ tooltipContent(p.profesional) }}
                        </div>
                      </div>
                      <span v-else>
                        Sin presupuestos
                      </span>
                    </td>
                    <td>
                      <div class="tax-status-cell">
                        <span
                          :class="['tax-badge', getTipoPrestadorBadgeClass(p.profesional.tipo_prestador_tributario)]"
                        >
                          {{ formatTipoPrestadorTributario(p.profesional.tipo_prestador_tributario) }}
                        </span>
                        <span
                          v-if="p.profesional.tipo_prestador_tributario === 'independiente'"
                          :class="['tax-badge', getSiiVerificationBadgeClass(p.profesional.bhe_autorizacion_estado)]"
                        >
                          {{ formatSiiVerificationStatus(p.profesional.bhe_autorizacion_estado) }}
                        </span>
                        <span
                          v-else
                          class="tax-secondary-text"
                        >
                          No requiere BHE
                        </span>
                      </div>
                    </td>
                    <td class="text-end">
                      <router-link :to="'VerProfesional/'+p.profesional.id" class="btn btn-icon" title="Editar">
                        <b-icon icon="gear"></b-icon>
                      </router-link>
                      <button
                        class="btn btn-icon"
                        @click="resetPassword(p.profesional)"
                        title="Resetear Contraseña">
                        <b-icon icon="key"></b-icon>
                      </button>

                    </td>
                  </tr>
                  <tr v-if="!profesionalesFiltrados.length">
                    <td colspan="10" class="text-center text-muted">No se encontraron profesionales.</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="card-footer">
              <div class="footer-info">
                Mostrando {{ profesionalesFiltrados.length }} profesionales de los {{ profesionales.length }} encontrados
              </div>
              <div class="footer-actions">
                <button @click="showCrearModal" class="btn btn-primary">
                  Crear un nuevo profesional
                </button>
                <button v-if="isSuperadmin" @click="openInviteOnboardingModal" class="btn btn-success">
                  Invitar onboarding
                </button>
                <button @click="openGenerateContractModal" class="btn btn-secondary">
                  Generar Contrato
                </button>
              </div>
            </div>
          </div>
          <div v-else class="loading-container">
            Cargando...
          </div>
        </div>
      </div>
    </div>

    <!-- Generate Contract Modal -->
    <div 
      v-if="isGenerateModalVisible" 
      class="custom-modal-overlay" 
      @click.self="closeGenerateContractModal"
    >
      <div class="custom-modal">
        <h3>Generar Contrato</h3>
        <div class="contract-tabs">
          <button
            type="button"
            class="contract-tab"
            :class="{ active: activeContractTab === 'professional' }"
            @click="activeContractTab = 'professional'"
          >
            Profesional
          </button>
          <button
            type="button"
            class="contract-tab"
            :class="{ active: activeContractTab === 'business' }"
            @click="activeContractTab = 'business'"
          >
            Empresa
          </button>
        </div>
        <form @submit.prevent="generateContract">
          <div v-if="activeContractTab === 'professional'" class="form-group">
            <label for="nombreProfesional">Nombre del Profesional:</label>
            <input
              type="text"
              id="nombreProfesional"
              v-model="contractForm.nombre_profesional"
              class="form-control"
              placeholder="Ingrese el nombre completo del profesional"
              required
            >
          </div>
          <div v-if="activeContractTab === 'professional'" class="form-group">
            <label for="rutProfesional">RUT del Profesional:</label>
            <input
              type="text"
              id="rutProfesional"
              v-model="contractForm.rut_profesional"
              class="form-control"
              placeholder="Ingrese el RUT del profesional"
              required
            >
          </div>

          <div v-else>
            <div class="form-group">
              <label for="businessRazonSocial">Razón social empresa prestadora:</label>
              <input
                type="text"
                id="businessRazonSocial"
                v-model="businessContractForm.razon_social"
                class="form-control"
                placeholder="Ej: Servicios ABC SpA"
                required
              >
            </div>
            <div class="form-group">
              <label for="businessRutEmpresa">RUT empresa:</label>
              <input
                type="text"
                id="businessRutEmpresa"
                v-model="businessContractForm.rut_empresa"
                class="form-control"
                placeholder="Ej: 76.123.456-7"
                required
              >
            </div>
            <div class="form-group">
              <label for="businessRepresentante">Nombre representante legal:</label>
              <input
                type="text"
                id="businessRepresentante"
                v-model="businessContractForm.nombre_representante"
                class="form-control"
                placeholder="Ej: Ana Pérez"
                required
              >
            </div>
            <div class="form-group">
              <label for="businessRutRepresentante">RUT representante legal:</label>
              <input
                type="text"
                id="businessRutRepresentante"
                v-model="businessContractForm.rut_representante"
                class="form-control"
                placeholder="Ej: 12.345.678-9"
                required
              >
            </div>
          </div>
          <div class="form-actions">
            <button type="submit" class="btn btn-primary" :disabled="loading">
              Descargar Contrato
            </button>
            <button 
              type="button" 
              class="btn btn-secondary" 
              @click="closeGenerateContractModal" 
              :disabled="loading"
            >
              Cancelar
            </button>
          </div>
        </form>
        <div v-if="loading" class="loading-indicator">
          Cargando...
        </div>
        <div v-if="errorMessage" class="error-message">
          {{ errorMessage }}
        </div>
      </div>
    </div>
      <div
      v-if="isInviteModalVisible"
      class="custom-modal-overlay"
      @click.self="closeInviteOnboardingModal"
    >
      <div class="custom-modal">
        <h3>Invitar onboarding</h3>
        <form @submit.prevent="submitOnboardingInvite">
          <div class="form-group">
            <label for="inviteNombre">Nombre</label>
            <input id="inviteNombre" v-model="onboardingInviteForm.nombre" type="text" class="form-control" required>
          </div>
          <div class="form-group">
            <label for="inviteApellido">Apellido</label>
            <input id="inviteApellido" v-model="onboardingInviteForm.apellido" type="text" class="form-control" required>
          </div>
          <div class="form-group">
            <label for="inviteEmail">Email</label>
            <input id="inviteEmail" v-model="onboardingInviteForm.email" type="email" class="form-control" required>
          </div>
          <div class="form-group">
            <label for="inviteTipo">Tipo profesional</label>
            <select id="inviteTipo" v-model="onboardingInviteForm.tipo_profesional" class="form-control" required>
              <option value="independiente">Profesional independiente</option>
              <option value="con_empresa">Profesional con empresa</option>
            </select>
          </div>
          <div class="form-actions">
            <button type="submit" class="btn btn-success" :disabled="inviteLoading">Enviar invitaci�n</button>
            <button type="button" class="btn btn-secondary" :disabled="inviteLoading" @click="closeInviteOnboardingModal">Cancelar</button>
          </div>
          <div v-if="inviteError" class="error-message">{{ inviteError }}</div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
import SideNav from '@/components/SideNav.vue';
import TopNav from '@/components/TopNav.vue';
import ModalNuevoProfesional from '@/components/ModalNuevoProfesional.vue';
// import ModalEditarComision from '@/components/ModalEditarComision.vue'; // REMOVED

export default {
  name: 'Profesionales',
  props: ['id'],
  components: {
    SideNav,
    TopNav,
    ModalNuevoProfesional,
    // ModalEditarComision, // REMOVED
  },
  data() {
    const today = new Date();
    const monthName = today.toLocaleString('es-CL', { month: 'long' });
    const defaultBusinessDate = {
      dia: String(today.getDate()),
      mes: monthName.charAt(0).toUpperCase() + monthName.slice(1),
    };

    return {
      loaded: false,
      profesionales: [],
      busqueda: '',
      isGenerateModalVisible: false,
      activeContractTab: 'professional',
      contractForm: {
        nombre_profesional: '',
        rut_profesional: '',
      },
      businessContractForm: {
        ...defaultBusinessDate,
        razon_social: '',
        rut_empresa: '',
        nombre_representante: '',
        rut_representante: '',
      },
      isInviteModalVisible: false,
      onboardingInviteForm: {
        nombre: '',
        apellido: '',
        email: '',
        tipo_profesional: 'independiente',
      },
      inviteLoading: false,
      inviteError: '',
      loading: false,
      errorMessage: '',
    };
  },
  created() {
    this.initialize_();
  },
  methods: {
    ...mapActions(['initialize']),
    getDefaultBusinessContractDate() {
      const today = new Date();
      const monthName = today.toLocaleString('es-CL', { month: 'long' });
      return {
        dia: String(today.getDate()),
        mes: monthName.charAt(0).toUpperCase() + monthName.slice(1),
      };
    },
    showCrearModal() {
      this.$refs.modalnuevoprofesional.show();
    },
    openInviteOnboardingModal() {
      this.onboardingInviteForm = {
        nombre: '',
        apellido: '',
        email: '',
        tipo_profesional: 'independiente',
      };
      this.inviteError = '';
      this.inviteLoading = false;
      this.isInviteModalVisible = true;
    },
    closeInviteOnboardingModal() {
      this.isInviteModalVisible = false;
      this.inviteLoading = false;
      this.inviteError = '';
    },
    submitOnboardingInvite() {
      const payload = {
        nombre: (this.onboardingInviteForm.nombre || '').trim(),
        apellido: (this.onboardingInviteForm.apellido || '').trim(),
        email: (this.onboardingInviteForm.email || '').trim().toLowerCase(),
        tipo_profesional: this.onboardingInviteForm.tipo_profesional,
      };

      if (!payload.nombre || !payload.apellido || !payload.email || !payload.tipo_profesional) {
        this.inviteError = 'Debes completar todos los campos.';
        return;
      }

      this.inviteLoading = true;
      this.inviteError = '';
      axios.post(`${window.hostname}api/superadmin/onboarding/invite`, payload)
        .then(() => {
          alert('Invitaci�n enviada correctamente.');
          this.closeInviteOnboardingModal();
        })
        .catch(error => {
          const msg = (error && error.response && error.response.data && error.response.data.message)
            ? error.response.data.message
            : 'No se pudo enviar la invitaci�n.';
          this.inviteError = msg;
        })
        .finally(() => {
          this.inviteLoading = false;
        });
    },
    initialize_() {
      this.loaded = false;
      axios.get(`${window.hostname}api/admin/getprofesionales`)
        .then(response => {
          this.loaded = true;
          this.profesionales = response.data.profesionales.map(item => {
            const profesionalData = item.profesional || {};
            if (!Array.isArray(profesionalData.clases_restringidas)) {
              profesionalData.clases_restringidas = [];
            }
            if (!Array.isArray(profesionalData.autoresponder_clases)) {
              profesionalData.autoresponder_clases = [];
            }
            return {
              ...item,
              profesional: profesionalData,
            };
          });
        })
        .catch(e => {
          this.loaded = true;
          console.error("Error al cargar profesionales:", e);
        });
    },
    // REMOVED showEditCommissionModal method
    // showEditCommissionModal(profesional) {
    //   this.$refs.modaleditarcomision.show(profesional);
    // },
    // REMOVED handleCommissionUpdated method
    // handleCommissionUpdated() {
    //   this.initialize_();
    // },
    openGenerateContractModal() {
      this.activeContractTab = 'professional';
      this.contractForm.nombre_profesional = '';
      this.contractForm.rut_profesional = '';
      this.businessContractForm = {
        ...this.getDefaultBusinessContractDate(),
        razon_social: '',
        rut_empresa: '',
        nombre_representante: '',
        rut_representante: '',
      };
      this.isGenerateModalVisible = true;
      this.errorMessage = '';
    },
    closeGenerateContractModal() {
      this.isGenerateModalVisible = false;
      this.loading = false;
      this.errorMessage = '';
    },
    generateContract() {
      let apiUrl = '';
      let filename = 'contrato.pdf';

      if (this.activeContractTab === 'professional') {
        const nombre_profesional = this.contractForm.nombre_profesional.trim();
        const rut_profesional = this.contractForm.rut_profesional.trim();
        if (!nombre_profesional || !rut_profesional) {
          this.errorMessage = 'El nombre y RUT del profesional son requeridos.';
          return;
        }
        apiUrl = `${window.hostname}api/superadmin/generate_contract?nombre_profesional=${encodeURIComponent(nombre_profesional)}&rut_profesional=${encodeURIComponent(rut_profesional)}`;
        filename = `acuerdo_${nombre_profesional.replace(/ /g, '_')}.pdf`;
      } else {
        const defaultDate = this.getDefaultBusinessContractDate();
        const payload = {
          dia: `${defaultDate.dia}`.trim(),
          mes: defaultDate.mes.trim(),
          razon_social: this.businessContractForm.razon_social.trim(),
          rut_empresa: this.businessContractForm.rut_empresa.trim(),
          nombre_representante: this.businessContractForm.nombre_representante.trim(),
          rut_representante: this.businessContractForm.rut_representante.trim(),
        };

        const allFieldsCompleted = Object.values(payload).every(value => value);
        if (!allFieldsCompleted) {
          this.errorMessage = 'Todos los campos de empresa son requeridos.';
          return;
        }

        const query = new URLSearchParams(payload).toString();
        apiUrl = `${window.hostname}api/superadmin/generate_business_contract?${query}`;
        filename = `contrato_empresa_${payload.razon_social.replace(/ /g, '_')}.pdf`;
      }

      this.loading = true;
      this.errorMessage = '';
      axios.get(apiUrl, { responseType: 'blob' })
      .then(response => {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.closeGenerateContractModal();
      })
      .catch(error => {
        console.error("Error al generar el contrato:", error);
        if (error.response && error.response.data) {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const json = JSON.parse(reader.result);
              this.errorMessage = json.error || "Ocurrió un error al generar el contrato.";
            } catch (e) {
              this.errorMessage = "Ocurrió un error al generar el contrato.";
            }
          };
          reader.readAsText(error.response.data);
        } else {
          this.errorMessage = "Ocurrió un error al generar el contrato. Por favor, inténtalo de nuevo.";
        }
      })
      .finally(() => {
        this.loading = false;
      });
    },
    formatRestrictedClassName(className) {
      if (!className || typeof className !== 'string') {
        return '';
      }
      if (className === 'desconocida') {
        return 'Desconocida';
      }
      if (className === 'sin_identificar') {
        return 'Sin identificar';
      }
      return className.charAt(0).toUpperCase() + className.slice(1);
    },
    formatTipoPrestadorTributario(value) {
      if (value === 'independiente') {
        return 'Independiente';
      }
      if (value === 'empresa') {
        return 'Empresa';
      }
      return 'Sin clasificar';
    },
    formatSiiVerificationStatus(value) {
      if (value === 'autorizacion_verificada') {
        return 'SII verificado';
      }
      if (
        value === 'autorizacion_fallida' ||
        value === 'autorizacion_expirada' ||
        value === 'autorizacion_revocada_o_invalida'
      ) {
        return 'SII no verificado';
      }
      return 'SII pendiente';
    },
    getTipoPrestadorBadgeClass(value) {
      if (value === 'independiente') {
        return 'tax-badge-independent';
      }
      if (value === 'empresa') {
        return 'tax-badge-company';
      }
      return 'tax-badge-unknown';
    },
    getSiiVerificationBadgeClass(value) {
      if (value === 'autorizacion_verificada') {
        return 'tax-badge-verified';
      }
      if (
        value === 'autorizacion_fallida' ||
        value === 'autorizacion_expirada' ||
        value === 'autorizacion_revocada_o_invalida'
      ) {
        return 'tax-badge-not-verified';
      }
      return 'tax-badge-pending';
    },
    // Returns the ratio text for presupuestos accepted vs total
    ratioText(prof) {
      return `${prof.presupuesto_aceptado_count} / ${prof.presupuesto_count} (${(prof.presupuesto_aceptado_count / prof.presupuesto_count).toFixed(3)*100}%)`;
    },
    // Returns a tooltip string with detailed presupuesto info
    tooltipContent(prof) {
      return `Enviados: ${prof.presupuesto_enviado_count}, Aceptados: ${prof.presupuesto_aceptado_count}, Rechazados: ${prof.presupuesto_rechazado_count}, Total: ${prof.presupuesto_count}`;
    },
    hasFraudScore(prof) {
      return prof && prof.fraud_leakage_score !== null && prof.fraud_leakage_score !== undefined;
    },
    formatFraudScore(prof) {
      if (!this.hasFraudScore(prof)) {
        return '';
      }
      return `${prof.fraud_leakage_score.toFixed(2)}`;
    },
    getJobbingPointsBalance(prof) {
      if (!prof) {
        return 0;
      }
      return prof.jobbing_points_balance ?? prof.balance_puntos_jobbing ?? 0;
    },
    formatJobbingPoints(value) {
      const points = Number(value || 0);
      if (!Number.isFinite(points)) {
        return '0';
      }
      return new Intl.NumberFormat('es-CL').format(points);
    },
    // New method to reset a professional's password
    resetPassword(profesional) {
      if (!confirm(`¿Estás seguro de que deseas restablecer la contraseña de ${profesional.nombre} ${profesional.apellido}?`)) {
        return;
      }
      axios.post(`${window.hostname}api/admin/reset_profesional_password`, { email: profesional.email })
        .then(response => {
          alert("Contraseña restablecida correctamente. Se ha enviado un correo con la nueva contraseña.");
        })
        .catch(error => {
          console.error("Error reseteando la contraseña:", error);
          alert("Hubo un error al restablecer la contraseña. Inténtalo de nuevo.");
        });
    },
  },
  computed: {
    ...mapGetters(['admin_datos']),
    profesionalesFiltrados() {
      const busqueda = this.busqueda.trim().toLowerCase();
      if (!busqueda) {
        return this.profesionales;
      }
      const telefonoBuscado = /^[+\d\s().-]+$/.test(busqueda) ? busqueda.replace(/\D/g, '') : '';
      return this.profesionales.filter(({ profesional }) => {
        const nombre = `${profesional.nombre || ''} ${profesional.apellido || ''}`.toLowerCase();
        const telefono = String(profesional.telefono || '').replace(/\D/g, '');
        return nombre.includes(busqueda) || Boolean(telefonoBuscado && telefono.includes(telefonoBuscado));
      });
    },
    isSuperadmin() {
      return Boolean(this.admin_datos && this.admin_datos.superadmin);
    },
  },
  mounted() {
    this.$root.$on('update-profesionales', () => {      
      this.initialize_();
    });
  },
};
</script>

<style scoped>
.css-tooltip { 
  position: relative;
  display: inline-block;
  cursor: pointer;
}

.restricted-class-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.restricted-class-badge {
  background-color: #6c5ce7;
  color: #fff;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  font-size: 0.75rem;
}

.tooltip-content {
  visibility: hidden;
  width: max-content;
  max-width: 200px;
  background-color: rgba(0, 0, 0, 0.8);
  color: #fff;
  text-align: center;
  padding: 5px 8px;
  border-radius: 4px;
  position: absolute;
  z-index: 100;
  bottom: 125%; /* positions tooltip above the text */
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  transition: opacity 0.3s;
  font-size: 12px;
}

/* Optional arrow for the tooltip */
.tooltip-content::after {
  content: "";
  position: absolute;
  top: 100%; /* bottom of tooltip */
  left: 50%;
  transform: translateX(-50%);
  border-width: 5px;
  border-style: solid;
  border-color: rgba(0, 0, 0, 0.8) transparent transparent transparent;
}

.css-tooltip:hover .tooltip-content {
  visibility: visible;
  opacity: 1;
}

.layout {
  display: flex;
}

.side-nav {
  width: 250px;
}

.main-content {
  flex: 1;
  background-color: #f2f7fb;
  padding: 80px 20px 20px 20px;
}

.main-container {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.content-card {
  margin-bottom: 20px;
}

.card-header {
  border-bottom: 1px solid #eaeaea;
  padding-bottom: 10px;
  margin-bottom: 15px;
}

.card-body {
  overflow-x: auto;
}

.table {
  width: 100%;
  margin-bottom: 0;
}

.avatar {
  height: 50px;
  width: 50px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 10px;
}

.prof-name {
  font-weight: 600;
  vertical-align: middle;
}

.tax-status-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
}

.points-balance {
  font-weight: 700;
  color: #1f7a3d;
}

.points-unit {
  color: #6c757d;
  font-weight: 700;
  margin-left: 0.25rem;
}

.tax-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.1;
}

.tax-badge-independent {
  background: #e6f4ea;
  color: #1f7a3d;
}

.tax-badge-company {
  background: #e8f0fe;
  color: #1a5bb8;
}

.tax-badge-unknown {
  background: #f1f3f5;
  color: #5f6368;
}

.tax-badge-verified {
  background: #eaf7ef;
  color: #20744a;
}

.tax-badge-pending {
  background: #fff5e6;
  color: #a56600;
}

.tax-badge-not-verified {
  background: #fdecea;
  color: #b3261e;
}

.tax-secondary-text {
  font-size: 0.8rem;
  color: #6c757d;
}

.star {
  color: #DAA520;
  margin-right: 2px;
}

.badge {
  padding: 5px 10px;
  border-radius: 12px;
  font-size: 12px;
  margin-right: 5px;
}

.badge-success {
  background-color: #2ECC40;
  color: #fff;
}

.badge-info {
  background-color: #0074D9;
  color: #fff;
}

.badge-warning {
  background-color: #e74c3c;
  color: #fff;
}

.badge-secondary {
  background-color: #422db4;
  color: #fff;
}
.badge-third {
  background-color: #17a2b8;
  color: #fff;
}

.badge-dark {
  background-color: #282828;
  color: #fff;
}

.badge-points {
  background-color: #1f7a3d;
  color: #fff;
}

.suspended-row {
  background-color: #f8d7da;
}

.text-end {
  text-align: right;
}

.footer-info {
  font-size: 14px;
  color: #777;
}

.footer-actions {
  margin-top: 15px;
  display: flex;
  gap: 10px;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.btn-primary {
  background-color: #007bff;
  color: #fff;
}

.btn-secondary {
  background-color: #6c757d;
  color: #fff;
}

.btn-success {
  background-color: #28a745;
  color: #fff;
}

.btn-icon {
  background: none;
  border: none;
  cursor: pointer;
}

.loading-container {
  text-align: center;
  padding: 20px;
}

.custom-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.custom-modal {
  background: #fff;
  padding: 30px;
  border-radius: 8px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.contract-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.contract-tab {
  flex: 1;
  border: 1px solid #d8d8d8;
  background: #f6f6f6;
  color: #333;
  border-radius: 4px;
  padding: 8px 10px;
  font-weight: 600;
}

.contract-tab.active {
  border-color: #007bff;
  background: #007bff;
  color: #fff;
}

.custom-modal h3 {
  text-align: center;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
}

.form-control {
  width: 100%;
  padding: 8px;
  box-sizing: border-box;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.loading-indicator,
.error-message {
  text-align: center;
  margin-top: 10px;
}

.error-message {
  color: #dc3545;
}
</style>
